/**
 * DatePicker — token-native date picker (trigger + calendar popover).
 *
 * Replaces MUI's <DatePicker>, which reads MUI's own theme (hardwired
 * to light app-wide) and so rendered light in dark mode. This one is
 * pure CSS tokens + date-fns, and portals its calendar to <body> so an
 * ancestor card's overflow:hidden can't clip it.
 *
 *   <DatePicker value={date} onChange={setDate} />
 *   <DatePicker value={d} onChange={setD}
 *     shouldDisableDate={(d) => d.getMonth() === 1 && d.getDate() === 29} />
 *
 * value/onChange use native Date objects (or null), matching the prior
 * MUI + AdapterDateFns contract so call sites don't change shape.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { cn } from '../cn';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// A truthy-but-invalid Date (e.g. new Date(undefined)) is still an
// object, so `value || new Date()` wouldn't fall back. Guard on the
// timestamp so an invalid value never reaches the calendar math
// (eachDayOfInterval throws a RangeError on an Invalid Date).
const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

const CalendarIcon = (
  <svg
    aria-hidden
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

export default function DatePicker({
  value = null,
  onChange,
  shouldDisableDate,
  placeholder = 'Select date',
  displayFormat = 'MMM d, yyyy',
  // Defaults to false to match the MUI DatePicker this replaced (it
  // had no clear button). Pages that store a nullable date and guard
  // their format()/submit paths can opt in with clearable.
  clearable = false,
  disabled = false,
  id,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => (isValidDate(value) ? value : new Date()));
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // When opening, jump the calendar to the selected month (or today).
  // Intentionally keyed on `open` only — we don't want to fight the
  // user's month navigation while the popover is open.
  useEffect(() => {
    if (open) setViewMonth(isValidDate(value) ? value : new Date());
  }, [open]);

  // Portaled, fixed-positioned calendar (so cards can't clip it).
  const updateRect = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect((prev) =>
      prev && prev.top === r.top && prev.left === r.left && prev.width === r.width
        ? prev
        : r,
    );
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Calendar grid: full weeks spanning the visible month.
  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth));
    const gridEnd = endOfWeek(endOfMonth(viewMonth));
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const valid = isValidDate(value);

  return (
    <div ref={wrapperRef} className={cn('relative', className)} style={{ minWidth: 0 }}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="flex items-center w-full transition-all duration-150"
        style={{
          gap: 8,
          padding: '9px 12px',
          background: 'rgb(var(--color-cream))',
          border: '1px solid rgb(var(--color-sand))',
          borderRadius: 8,
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: 13.5,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
          if (disabled) return;
          e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
        }}
      >
        <span className="text-text-muted shrink-0">{CalendarIcon}</span>
        <span className="flex-1 min-w-0 block truncate">
          {valid ? (
            <span className="text-navy">{format(value, displayFormat)}</span>
          ) : (
            <span className="text-text-muted">{placeholder}</span>
          )}
        </span>
        {clearable && valid && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear date"
            className="text-text-muted shrink-0"
            style={{ fontSize: 14, lineHeight: 1, padding: '0 2px' }}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(null);
            }}
          >
            ×
          </span>
        )}
      </button>

      {open && rect && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: rect.bottom + 6,
            left: rect.left,
            width: 268,
            maxWidth: 'calc(100vw - 16px)',
            background: 'rgb(var(--color-surface))',
            border: '1px solid rgb(var(--color-sand))',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgb(var(--color-navy) / 0.12)',
            padding: 12,
            zIndex: 1000,
          }}
        >
          {/* Month nav */}
          <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setViewMonth((m) => subMonths(m, 1))}
              className="grid place-items-center text-text-muted hover:text-navy"
              style={{ width: 26, height: 26, borderRadius: 6, border: 0, background: 'transparent', cursor: 'pointer' }}
            >
              ‹
            </button>
            <span className="text-navy font-semibold" style={{ fontSize: 13 }}>
              {format(viewMonth, 'MMMM yyyy')}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="grid place-items-center text-text-muted hover:text-navy"
              style={{ width: 26, height: 26, borderRadius: 6, border: 0, background: 'transparent', cursor: 'pointer' }}
            >
              ›
            </button>
          </div>

          {/* Weekday header */}
          <div
            className="text-text-muted"
            style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', fontSize: 10.5, marginBottom: 4 }}
          >
            {WEEKDAYS.map((w) => (
              <span key={w} className="text-center">{w}</span>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {days.map((d) => {
              const inMonth = isSameMonth(d, viewMonth);
              const selected = valid && isSameDay(d, value);
              const today = isToday(d);
              const isDisabled = disabled || (shouldDisableDate ? shouldDisableDate(d) : false);
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    onChange?.(d);
                    setOpen(false);
                  }}
                  className="grid place-items-center"
                  style={{
                    height: 30,
                    borderRadius: 6,
                    border: today && !selected ? '1px solid rgb(var(--color-teal-green) / 0.5)' : 0,
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    background: selected ? 'rgb(var(--color-teal-green))' : 'transparent',
                    color: selected
                      ? '#fff'
                      : isDisabled
                        ? 'rgb(var(--color-text-muted) / 0.5)'
                        : inMonth
                          ? 'rgb(var(--color-navy))'
                          : 'rgb(var(--color-text-muted))',
                    opacity: !inMonth && !selected ? 0.6 : 1,
                    transition: 'background 120ms ease',
                  }}
                  onMouseEnter={(e) => {
                    if (selected || isDisabled) return;
                    e.currentTarget.style.background = 'rgb(var(--color-cream))';
                  }}
                  onMouseLeave={(e) => {
                    if (selected || isDisabled) return;
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
